import { PLAYER } from "../../../common/constants.ts";
import {
	derivePositionSalaryMap,
	type PositionSalaryDatum,
} from "../../../common/positionSalary.ts";
import { team } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import { addMood, freeAgentStats } from "./freeAgents.ts";

// G5-1: derive the league-wide position -> salary distribution the negotiation
// cards reference. Read-only: iterate the already-cached players, keep only
// rostered ones (tid >= 0) with a real contract, and bucket their per-year pay
// (millions, matching getNegotiationAskingAmount) by position. Cheap enough to
// recompute per view render; builds no persistent structure.
// The return type is spelled out structurally (not the PositionSalaryMap alias)
// so the generated View type (genApiTypes only resolves named refs from its
// COMMON_SOURCES list) inlines it cleanly; the UI re-imports the named alias.
const getPositionSalaryMap = async (): Promise<
	Record<string, { median: number; p25: number; p75: number; count: number }>
> => {
	const allPlayers = await idb.cache.players.getAll();
	const data: PositionSalaryDatum[] = [];
	for (const p of allPlayers) {
		if (p.tid < 0) {
			continue;
		}
		const pos = p.ratings.at(-1)?.pos;
		const amount = p.contract?.amount;
		if (typeof pos === "string" && typeof amount === "number") {
			data.push({ pos, amount: amount / 1000 });
		}
	}
	return derivePositionSalaryMap(data);
};

export const getNegotiationPids = async (tid: number) => {
	const negotiations = await idb.cache.negotiations.getAll();

	// Need to check tid for Multi Team Mode, might have other team's negotiations going on
	return new Set(
		negotiations
			.filter((negotiation) => negotiation.tid === tid)
			.map((negotiation) => negotiation.pid),
	);
};

const updateNegotiationList = async () => {
	const stats = ["yearsWithTeam", ...freeAgentStats];

	const userTid = g.get("userTid");

	const negotiationPids = await getNegotiationPids(userTid);

	const userPlayersAll = await idb.cache.players.indexGetAll(
		"playersByTid",
		userTid,
	);
	const playersAll = await addMood(
		(
			await idb.cache.players.indexGetAll("playersByTid", PLAYER.FREE_AGENT)
		).filter((p) => negotiationPids.has(p.pid)),
	);

	const players = addFirstNameShort(
		await idb.getCopies.playersPlus(playersAll, {
			attrs: [
				"pid",
				"firstName",
				"lastName",
				"age",
				"injury",
				"jerseyNumber",
				"watch",
				"contract",
				"draft",
				"latestTransaction",
				"latestTransactionSeason",
				"mood",
			],
			ratings: ["ovr", "pot", "skills", "pos"],
			stats,
			season: g.get("season"),
			tid: userTid,
			showNoStats: true,
			fuzz: true,
		}),
	);

	let sumContracts = 0;
	for (const p of players) {
		sumContracts += p.mood.user.contractAmount;
	}
	sumContracts /= 1000;

	const payroll = await team.getPayroll(userTid);
	const capSpace = (g.get("salaryCap") - payroll) / 1000;

	const positionSalary = await getPositionSalaryMap();

	const userPlayers = await idb.getCopies.playersPlus(userPlayersAll, {
		attrs: [],
		ratings: ["pos"],
		stats: [],
		season: g.get("season"),
		showNoStats: true,
		showRookies: true,
	});

	return {
		capSpace,
		challengeNoRatings: g.get("challengeNoRatings"),
		draftPickAutoContract: g.get("draftPickAutoContract"),
		luxuryPayroll: g.get("luxuryPayroll") / 1000,
		salaryCapType: g.get("salaryCapType"),
		maxContract: g.get("maxContract"),
		minContract: g.get("minContract"),
		numRosterSpots: g.get("maxRosterSize") - userPlayersAll.length,
		positionSalary,
		spectator: g.get("spectator"),
		payroll: payroll / 1000,
		players,
		season: g.get("season"),
		stats,
		sumContracts,
		userPlayers,
	};
};

export default updateNegotiationList;
